import { useEffect, useRef } from 'react'

// Auto-mark-as-read on scroll-past with a dwell threshold.
//
// Three signals decide when to mark a card:
//   - VISIBILITY_THRESHOLD: how much of the card must be on screen
//     before we start counting dwell. 0.25 instead of 0.5 because
//     tall Reddit cards with embedded media often max out at ~40%
//     viewport coverage on small screens, never reaching 50%.
//   - DWELL_MS: how long the card must be visible before it counts
//     as "seen". Filters out flick-scrolls.
//   - past-the-top exit: card's bottom edge has gone above the
//     scroller's top edge — i.e., the user has scrolled DOWN past
//     it, not bounced UP.
//
// CRITICAL: the app's scroll container is a div with overflow:auto
// inside the viewport, NOT the document itself. IntersectionObserver
// with default root: null watches the BROWSER VIEWPORT — a card
// past the scroller's top edge is still inside the viewport (just
// clipped by overflow), so the observer never reports it as out.
// We walk up to find the nearest scrolling ancestor and pass it as
// `root`. boundingClientRect / rootBounds stay viewport-relative
// either way, so the "past the top" check compares the card's
// bottom against rootBounds.top.

const DWELL_MS = 1500
const VISIBILITY_THRESHOLD = 0.25

function findScrollableAncestor(el) {
  let p = el?.parentElement
  while (p && p !== document.body) {
    const style = window.getComputedStyle(p)
    if (/(auto|scroll|overlay)/.test(style.overflowY)) return p
    p = p.parentElement
  }
  return null
}

export function useReadOnScroll(ref, articleId, onRead, alreadyRead) {
  // Stash mutable state in a ref so the observer callback doesn't
  // restart its timer every time React re-renders the parent.
  const stateRef = useRef({ visibleSince: 0 })

  useEffect(() => {
    if (alreadyRead) return undefined
    if (!articleId) return undefined
    if (typeof IntersectionObserver === 'undefined') return undefined
    const el = ref.current
    if (!el) return undefined

    stateRef.current = { visibleSince: 0 }
    const scroller = findScrollableAncestor(el)

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        const s = stateRef.current
        const now = Date.now()

        // Card is comfortably on screen: start (or keep) the dwell
        // timer.
        if (entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
          if (!s.visibleSince) s.visibleSince = now
          return
        }

        // Below threshold. Three possibilities:
        //   1. Card scrolled past the TOP (user kept reading down).
        //      Commit if dwell met.
        //   2. Card scrolled past the BOTTOM (user reversed UP past
        //      it). Reset dwell — they didn't keep reading.
        //   3. Card is partially visible but below the threshold
        //      (e.g., a very tall card just barely on screen). KEEP
        //      the dwell going — don't reset prematurely.
        const r = entry.boundingClientRect
        const rb = entry.rootBounds
        if (!rb) return
        const dwellMs = s.visibleSince ? now - s.visibleSince : 0

        if (r.bottom <= rb.top + 4) {
          // Past the top — commit when dwell met. Either way,
          // disconnect; we're done with this card.
          if (dwellMs >= DWELL_MS) {
            onRead?.(articleId)
          }
          obs.disconnect()
          return
        }

        if (r.top >= rb.bottom - 4) {
          // Past the bottom — the user reversed past this card
          // without ever pausing on it long enough. Reset dwell so
          // a brief re-visit later doesn't compound the prior time.
          s.visibleSince = 0
          return
        }

        // Below threshold but not past either edge — partially
        // visible. Keep dwell intact.
      },
      {
        // Watch within the actual scroll container, not the viewport.
        root: scroller || null,
        threshold: [0, VISIBILITY_THRESHOLD],
      },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [ref, articleId, alreadyRead, onRead])
}
